import { Component, type ReactNode } from "react";
import { requestSceneRebuild } from "../render/pixi/gpuHealth";
import { reportClientError } from "../sim/clientHealth";

/**
 * ERROR BOUNDARIES — so a crash never leaves the player looking at an empty page.
 *
 * React unmounts the WHOLE tree on an uncaught render error. With no boundary anywhere, one bad render in a
 * scene (or in a modal opened on the first tick after a long absence) took the HUD, the menubar and the scene
 * with it, and the player saw a blank page with nothing to click.
 *
 *   • SceneBoundary wraps the mounted scene: a crash there asks gpuHealth for a REBUILD (App keys the scene on
 *     the GPU epoch, so the rebuild remounts this boundary too, fresh). Until then it renders nothing, and the
 *     HUD stays up. If rebuilding keeps failing, gpuHealth offers the reload (GpuResetNotice).
 *   • AppBoundary wraps everything: the last line, a plain card with a Reload button.
 */
export class SceneBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } { return { failed: true }; }
  componentDidCatch(error: unknown): void {
    console.error("[scene] the scene crashed — rebuilding it:", error);
    reportClientError("react", error);
    requestSceneRebuild("the scene crashed");
  }
  render(): ReactNode { return this.state.failed ? null : this.props.children; }
}

export class AppBoundary extends Component<{ children: ReactNode }, { error: string | null }> {
  state = { error: null as string | null };
  static getDerivedStateFromError(e: unknown): { error: string } { return { error: e instanceof Error ? e.message : String(e) }; }
  componentDidCatch(error: unknown): void { console.error("[app] the page crashed:", error); reportClientError("react", error); }
  render(): ReactNode {
    if (this.state.error === null) return this.props.children;
    return (
      <div className="app-crash" role="alert">
        <div className="app-crash-card">
          <div className="app-crash-title">Something went wrong</div>
          <p>The game hit an error and can't continue. Your progress is saved, so reloading will put you back where you left off.</p>
          <button className="app-crash-btn" onClick={() => window.location.reload()}>Reload</button>
        </div>
      </div>
    );
  }
}

/** The graphics could not START (gpuHealth `graphicsUnavailable` — no WebGL): a reload will not fix it, so the
 *  card says what will, instead of leaving the player in front of an empty scene. */
export function GraphicsUnavailableNotice() {
  return (
    <div className="gpu-reset" role="alert" data-testid="graphics-unavailable">
      <div className="gpu-reset-card">
        <div className="gpu-reset-title">Your browser couldn’t start the game’s graphics</div>
        <p>
          Shelf Garden draws with WebGL, which isn’t available here. Turning on <b>hardware acceleration</b> in your
          browser’s settings usually fixes it. You can also try the latest Chrome, Edge or Firefox. Your progress is saved.
        </p>
        <button className="gpu-reset-btn" onClick={() => window.location.reload()}>Try again</button>
      </div>
    </div>
  );
}

/** TOUCH-ONLY DEVICE (a phone or tablet with no mouse). The garden and greenhouse take touch now — drag to look
 *  around, paint water, move decor, tap to act — so a touch player is welcomed once per session with how to play
 *  by touch, and told a bigger screen shows more of the garden. */
const TOUCH_SEEN_KEY = "sg:touch-notice";
export function isTouchOnly(): boolean {
  try { return window.matchMedia("(pointer: coarse)").matches && !window.matchMedia("(any-pointer: fine)").matches; }
  catch { return false; }
}
export function touchNoticeSeen(): boolean {
  try { return sessionStorage.getItem(TOUCH_SEEN_KEY) === "1"; } catch { return false; }
}
export function TouchOnlyNotice({ onClose }: { onClose: () => void }) {
  const close = () => { try { sessionStorage.setItem(TOUCH_SEEN_KEY, "1"); } catch { /* private mode */ } onClose(); };
  return (
    <div className="gpu-reset" role="dialog" aria-label="Touch controls" data-testid="touch-notice">
      <div className="gpu-reset-card">
        <div className="gpu-reset-title">Playing on a phone</div>
        <p>
          Drag with one finger to look around and tap for everything else.
          A bigger screen shows more of your garden.
        </p>
        <button className="gpu-reset-btn" onClick={close}>Start playing</button>
      </div>
    </div>
  );
}

/** The GPU is gone for good (gpuHealth `gpuFatal`): say so plainly and offer the reload, over the scene. */
export function GpuResetNotice() {
  return (
    <div className="gpu-reset" role="alert">
      <div className="gpu-reset-card">
        <div className="gpu-reset-title">The graphics need a refresh</div>
        <p>Your browser reset the game’s graphics while it was in the background. Your progress is saved.</p>
        <button className="gpu-reset-btn" onClick={() => window.location.reload()}>Reload the game</button>
      </div>
    </div>
  );
}
